import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

function parseJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    if (storedToken) {
      try {
        const decoded = parseJwt(storedToken);
        // Check token not expired
        if (decoded && decoded.exp * 1000 > Date.now()) {
          setToken(storedToken);
          // Merge stored user with JWT claims so user_type is always present
          const base = storedUser ? JSON.parse(storedUser) : {};
          setUser({ ...base, ...decoded });
        } else {
          // Token expired — clear storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        }
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = (tokenValue, userData) => {
    const decoded = parseJwt(tokenValue);
    // Merge DB user data with JWT claims — JWT always has user_type
    const mergedUser = { ...userData, ...(decoded || {}) };
    localStorage.setItem('auth_token', tokenValue);
    localStorage.setItem('auth_user', JSON.stringify(mergedUser));
    setToken(tokenValue);
    setUser(mergedUser);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  };

  const authFetch = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    };
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      logout();
      throw new Error('Session expired. Please log in again.');
    }
    return res;
  };

  const isAdmin = user?.user_type?.toLowerCase() === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, login, logout, authFetch, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
